import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useSession } from '@/features/auth/api/useSession'
import { isLanguage, type Language } from '@/i18n/config'

/* Keeps three things saying the same thing: the user's stored preference, the
 * i18next instance, and the document's `lang` attribute.
 *
 * The SERVER is the home of the preference (`CurrentUser.language`, FR-58) so
 * it follows the user between machines. localStorage is only a pre-session
 * cache, so the first paint after a reload is not in the wrong language while
 * the session query is in flight — i18next's detector reads it synchronously.
 *
 * `<html lang>` is not decoration: it selects the hyphenation and quotation
 * rules the browser applies, and it is what a screen reader reads the page in.
 * A Russian page announced by an English voice is unusable, and this is the one
 * line that prevents it.
 */

/** The active UI language, following the session once it resolves. */
export function useLanguage(): Language {
  const { i18n } = useTranslation()
  const { data: user } = useSession()

  const current: Language = isLanguage(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : 'en'
  const preferred = user?.language

  useEffect(() => {
    // The session is authoritative. Until it resolves, whatever the detector
    // picked stands — switching twice would flash the language on every load.
    if (preferred && isLanguage(preferred) && preferred !== current) {
      void i18n.changeLanguage(preferred)
    }
  }, [preferred, current, i18n])

  useEffect(() => {
    document.documentElement.lang = current
  }, [current])

  return current
}

/* There is deliberately no locale-tag hook here. A component that only wants to
 * format in the active language reads it from i18next through
 * `i18n/useLocaleTag`, which needs no session — see that file for why. */
